import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { calendarEventService } from '../../services/calendarEvent';
import { CalendarEvent } from '../../types';
import { Input, Select } from '../../../../components';
import { Button } from '../../../../components/Button';
import { Badge } from '../../../../components';

export const EventList: React.FC = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter] = useState<'all' | 'upcoming' | 'past'>('all');
  const [search, setSearch] = useState('');
  const [provider, setProvider] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await calendarEventService.list({
        is_upcoming: filter === 'upcoming' ? true : undefined,
        is_past: filter === 'past' ? true : undefined,
        provider: provider || undefined,
        status: statusFilter || undefined,
        search: search || undefined,
        page,
        page_size: pageSize,
      });
      setEvents(response?.results || []);
      setTotalCount(response?.count || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('calendars.failedToLoadEvents'));
    } finally {
      setLoading(false);
    }
  }, [filter, provider, statusFilter, search, page, pageSize, t]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  if (loading) {
    return (
      <div className="text-center p-8 text-gray-600 dark:text-gray-300">{t('common.loading')}</div>
    );
  }

  if (error) {
    return <div className="text-center p-8 text-red-600 dark:text-red-400">{error}</div>;
  }

  return (
    <>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {t('calendars.calendarEvents')}
        </h2>
        <div className="flex flex-wrap gap-2 items-center">
          <Input
            placeholder={t('calendars.searchEvents')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-80 h-10"
          />
          <Select
            options={[
              { value: '', label: t('calendars.allProviders') },
              { value: 'google', label: 'Google' },
              { value: 'outlook', label: 'Outlook' },
              { value: 'apple', label: 'Apple' },
            ]}
            value={provider}
            onChange={val => setProvider(Array.isArray(val) ? (val[0] as string) : (val as string))}
            className="w-40 h-10"
          />
          <Select
            options={[
              { value: '', label: t('calendars.allStatus') },
              { value: 'pending', label: t('calendars.pending') },
              { value: 'synced', label: t('calendars.synced') },
              { value: 'failed', label: t('calendars.failed') },
              { value: 'conflict', label: t('calendars.conflict') },
            ]}
            value={statusFilter}
            onChange={val =>
              setStatusFilter(Array.isArray(val) ? (val[0] as string) : (val as string))
            }
            className="w-40 h-10"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('calendars.eventTitle')}
              </th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('calendars.provider')}
              </th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('calendars.start')}
              </th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('calendars.end')}
              </th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('calendars.status')}
              </th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-12">
                  <div className="text-center">
                    <span className="material-icons text-4xl text-gray-400 dark:text-gray-500">
                      event
                    </span>
                    <p className="text-gray-600 dark:text-gray-400 mt-4 text-lg">
                      {t('calendars.noEventsFound')}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                      {t('calendars.syncCalendarsToSeeEvents')}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              events.map(ev => (
                <tr
                  key={ev.id}
                  className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  <td className="py-3 px-4 text-sm text-gray-900 dark:text-white font-medium">
                    {ev.title}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                    {ev.calendar_provider}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                    {new Date(ev.start_datetime).toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                    {new Date(ev.end_datetime).toLocaleString()}
                  </td>
                  <td className="py-3 px-4">
                    <Badge
                      variant={
                        ev.status === 'synced'
                          ? 'success'
                          : ev.status === 'failed'
                            ? 'error'
                            : 'info'
                      }
                      size="sm"
                    >
                      {ev.status_display}
                    </Badge>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalCount > pageSize && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {t('calendars.pageOf', {
              current: page,
              total: Math.max(1, Math.ceil(totalCount / pageSize)),
            })}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              title={t('common.previous')}
              className="h-8 w-8 p-0"
            >
              <span className="material-icons text-base">chevron_left</span>
            </Button>

            {(() => {
              const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
              const pages: (number | 'ellipsis')[] = [];
              const add = (n: number) => {
                if (!pages.includes(n)) pages.push(n);
              };
              add(1);
              add(totalPages);
              add(page);
              add(page - 1);
              add(page + 1);
              const normalized = Array.from(
                new Set(
                  pages
                    .filter(p => typeof p === 'number' && p >= 1 && (p as number) <= totalPages)
                    .sort((a, b) => (a as number) - (b as number))
                )
              );
              const withEllipsis: (number | 'ellipsis')[] = [];
              for (let i = 0; i < normalized.length; i++) {
                const current = normalized[i] as number;
                const prev = normalized[i - 1] as number | undefined;
                if (prev !== undefined && current - prev > 1) {
                  withEllipsis.push('ellipsis');
                }
                withEllipsis.push(current);
              }
              return withEllipsis.map((p, idx) =>
                p === 'ellipsis' ? (
                  <span key={`e-${idx}`} className="px-2 text-sm text-gray-500 dark:text-gray-400">
                    …
                  </span>
                ) : (
                  <Button
                    key={p}
                    variant={p === page ? 'light' : 'gradient-multi'}
                    size="sm"
                    className="h-8 px-3"
                    onClick={() => setPage(p as number)}
                  >
                    {p}
                  </Button>
                )
              );
            })()}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage(p => (p * pageSize < totalCount ? p + 1 : p))}
              disabled={page * pageSize >= totalCount}
              title={t('common.next')}
              className="h-8 w-8 p-0"
            >
              <span className="material-icons text-base">chevron_right</span>
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
